import React, { createContext, useCallback, useReducer } from 'react';
import _merge from 'lodash-es/merge';

import { MetaData, PremiumOffer, Seller, VehicleDetails } from 'Utilities/profiling/@types';

export type VehicleProfilingState = {
  hasPaasProfile?: boolean;
  metaData?: MetaData;
  originalVrmFeatureFlag?: Record<string, boolean>;
  premiumOfferData?: PremiumOffer;
  prepCostReconfirm?: boolean;
  profilingUrlPath?: string;
  resupply?: boolean;
  sellerData?: Seller;
  serviceHistoryResupplied?: boolean;
  switchStart?: boolean;
}

enum VehicleProfilingActionType {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  UPDATE = 'UPDATE',
  UPDATE_DETAILS = 'UPDATE_DETAILS'
}

type VehicleProfilingAction = {
  payload: Partial<VehicleProfilingState > | Partial<VehicleDetails>;
  type: VehicleProfilingActionType;
}

type VehicleProfilingActions = {
  add: (payload: VehicleProfilingState) => void;
  remove: () => void;
  update: (payload: VehicleProfilingState) => void;
  updateDetails: (payload: Partial<VehicleDetails>) => void;
}

type VehicleProfilingContext = {
  vehicleProfilingActions: VehicleProfilingActions;
  vehicleProfilingState: VehicleProfilingState;
}

type VehicleProfilingProps = {
  children: React.ReactNode;
  value: VehicleProfilingState;
}

// Export dispatch available outside of React
const VehicleProfilingDispatch = {
  actions: {} as VehicleProfilingActions,
};

// Export state available outside of React
const VehicleProfilingStateStatic = {
  state: {} as VehicleProfilingState,
};

const initialActions = {
  add: () => { },
  remove: () => { },
  update: () => { },
  updateDetails: () => { },
};

const VehicleProfilingContext = createContext<VehicleProfilingContext>(
  { vehicleProfilingActions: initialActions, vehicleProfilingState: {} },
);

const reducer = (state: VehicleProfilingState, { payload, type }: VehicleProfilingAction) => {
  const oldState = state || {};
  let newState: VehicleProfilingState | {};

  switch (type) {
  case VehicleProfilingActionType.ADD: {
    // Only one vehicle at a time, so replace everything
    newState = payload;
    break;
  }
  case VehicleProfilingActionType.UPDATE: {
    newState = _merge({}, oldState, payload);
    break;
  }
  case VehicleProfilingActionType.UPDATE_DETAILS: {
    newState = {
      ...oldState,
      premiumOfferData: {
        ...oldState?.premiumOfferData,
        vehicleDetails: {
          ...oldState?.premiumOfferData?.vehicleDetails,
          ...payload,
        },
      },
    };
    break;
  }
  case VehicleProfilingActionType.REMOVE: {
    newState = {};
    break;
  }
    // no default
  }
  return newState;
};

const VehicleProfilingProvider = ({ children, value }: VehicleProfilingProps) => {
  const [vehicleProfilingState, dispatch] = useReducer(reducer, value || {});

  const vehicleProfilingActions = {
    add: (payload: VehicleProfilingState) => {
      if (payload?.premiumOfferData?.vehicle) {
        dispatch({ payload, type: VehicleProfilingActionType.ADD });
      }
    },
    remove: useCallback(() => {
      dispatch({ payload: {}, type: VehicleProfilingActionType.REMOVE });
    }, []),
    update: useCallback((payload: Partial<VehicleProfilingState>) => {
      dispatch({ payload, type: VehicleProfilingActionType.UPDATE });
    }, []),
    updateDetails: useCallback((payload: Partial<VehicleDetails>) => {
      dispatch({ payload, type: VehicleProfilingActionType.UPDATE_DETAILS });
    }, []),
  };

  // Expose dispatch and state from outside React,
  VehicleProfilingDispatch.actions = Object.freeze(vehicleProfilingActions);

  VehicleProfilingStateStatic.state = Object.freeze({
    ...vehicleProfilingState,
  });

  return (
    <VehicleProfilingContext.Provider
      value={{
        vehicleProfilingActions,
        vehicleProfilingState,
      }}
    >
      {children}
    </VehicleProfilingContext.Provider>
  );
};

export {
  VehicleProfilingContext,
  VehicleProfilingDispatch,
  VehicleProfilingProvider,
  VehicleProfilingStateStatic,
};
